import { Injectable, signal, effect } from '@angular/core';
import { AptitudeQuestion, AptitudeTask } from '@career-scope/models';
import { BaseAptitudeService } from './base-aptitude.service';

@Injectable()
export class ImagesService {

  images: string[] = [];
  loadedNum = 0;
  loaded = signal(false);

  private readonly BATCH_SIZE = 10;
  private batchIndex = 0;

  constructor(private as: BaseAptitudeService) {
    // Use effect() so we reactively wait for tasks to be available from Firestore
    // before starting preload — avoids the race condition where tasks were empty
    // at construction time and preloading silently never ran.
    effect(() => {
      const evalueeTasks = this.as.getTasks()();
      if (evalueeTasks.length !== 0 && !this.loaded() && this.images.length === 0) {
        this.populateImages(evalueeTasks);
      }
    });
  }

  populateImages(tasks: AptitudeTask[]): void {
    const icons: string[] = [];

    tasks.forEach(task => {
      if (task.iconFile) {
        icons.push('assets/images/icons/' + task.iconFile);
      }

      if (!task.complete) {
        // Warm only the entry point for each exercise. Loading every graphic for
        // every incomplete task here competes with the exercise the seeker chose.
        const initialQuestions = [
          task.practiceQuestions[0],
          task.questions[0]
        ].filter((question): question is AptitudeQuestion => Boolean(question));

        initialQuestions.forEach(question => {
          this.images.push(...this.getQuestionImageSources(task, question));
        });
      }
    });

    this.images = [...new Set(this.images)];
    this.preloadIcons(icons);

    if (this.images.length) {
      this.loadImages();
    } else {
      this.loaded.set(true);
    }
  }

  private preloadIcons(icons: string[]): void {
    icons.forEach(src => {
      const img = new Image();
      img.src = src;
      img.decode?.().catch(() => {});
    });
  }

  private getQuestionImageSources(
    task: AptitudeTask,
    question: AptitudeQuestion
  ): string[] {
    const files: string[] = [];

    if (task.questionType === 'Graphic' && question.question) {
      files.push(question.question);
    }
    if (task.choiceType === 'Graphic') {
      files.push(...question.choices);
    }

    return files.map(file =>
      `assets/images/${task.legacyCode}/${task.legacyCode}_${file}`
    );
  }

  loadImages(): void {
    // eslint-disable-next-line no-restricted-syntax
    console.log('Pre-loading', this.images.length, 'images');
    // eslint-disable-next-line no-restricted-syntax
    console.time('Loading');
    this.batchIndex = 0;
    this.loadNextBatch();
  }

  private loadNextBatch(): void {
    const batch = this.images.slice(this.batchIndex, this.batchIndex + this.BATCH_SIZE);
    if (!batch.length) {
      return;
    }

    this.batchIndex += this.BATCH_SIZE;
    let batchSettled = 0;

    batch.forEach(imgSource => {
      const img = new Image();
      let settled = false;
      const done = () => {
        if (settled) {
          return;
        }
        settled = true;
        this.imgLoaded();
        batchSettled++;
        if (batchSettled === batch.length) {
          this.loadNextBatch();
        }
      };

      img.onload = () => {
        img.decode?.().catch(() => undefined).then(done);
      };
      img.onerror = done;

      // Start loading after handlers are attached so cached loads are counted.
      img.src = imgSource;
    });
  }

  private imgLoaded(): void {
    this.loadedNum++;

    if (this.loadedNum === this.images.length) {
      this.loaded.set(true);
      // eslint-disable-next-line no-restricted-syntax
      console.timeEnd('Loading');
    }
  }
}
